import { useEffect, useRef, useState } from "react";
import { createChart, CandlestickSeries, HistogramSeries, createSeriesMarkers } from "lightweight-charts";
import { fetchKlines, fmtUsd } from "@/lib/api";

const STEP = { "15m": 900, "1h": 3600, "4h": 14400, "1d": 86400 };
const COLORS = { inflow: "#FF3D00", outflow: "#00E676" };

const buildMarkers = (transfers, step, first) => {
  const buckets = {};
  transfers
    .filter((t) => COLORS[t.direction] && t.ts >= first)
    .forEach((t) => {
      const time = Math.floor(t.ts / step) * step;
      const k = `${time}-${t.direction}`;
      buckets[k] = buckets[k] || { time, direction: t.direction, usd: 0 };
      buckets[k].usd += t.amount_usd;
    });
  return Object.values(buckets)
    .sort((a, b) => a.time - b.time)
    .map((b) => ({
      time: b.time,
      position: b.direction === "inflow" ? "aboveBar" : "belowBar",
      color: COLORS[b.direction],
      shape: b.direction === "inflow" ? "arrowDown" : "arrowUp",
      text: fmtUsd(b.usd),
    }));
};

export default function WhaleChart({ pair, cgId, interval, transfers }) {
  const box = useRef(null);
  const refs = useRef({});
  const [candles, setCandles] = useState([]);
  const [error, setError] = useState(null);

  useEffect(() => {
    const chart = createChart(box.current, {
      autoSize: true,
      localization: { locale: "en-US" },
      layout: { background: { color: "transparent" }, textColor: "#94A3B8", fontFamily: "JetBrains Mono" },
      grid: { vertLines: { color: "#161D2A" }, horzLines: { color: "#161D2A" } },
      rightPriceScale: { borderColor: "#1E2738" },
      timeScale: { borderColor: "#1E2738", timeVisible: true },
      crosshair: { mode: 0 },
    });
    const candle = chart.addSeries(CandlestickSeries, {
      upColor: "#00E676", downColor: "#FF3D00", borderVisible: false,
      wickUpColor: "#00E676", wickDownColor: "#FF3D00",
    });
    const vol = chart.addSeries(HistogramSeries, { priceScaleId: "vol", priceFormat: { type: "volume" }, color: "#2E3C54" });
    chart.priceScale("vol").applyOptions({ scaleMargins: { top: 0.82, bottom: 0 } });
    refs.current = { chart, candle, vol, markers: createSeriesMarkers(candle, []) };
    return () => chart.remove();
  }, []);

  useEffect(() => {
    let alive = true;
    const load = () =>
      fetchKlines(pair, interval, cgId)
        .then((d) => alive && (setCandles(d), setError(null)))
        .catch(() => alive && setError("Price feed unavailable"));
    load();
    const id = setInterval(load, 30000);
    return () => { alive = false; clearInterval(id); };
  }, [pair, cgId, interval]);

  useEffect(() => {
    const { candle, vol } = refs.current;
    if (!candle) return;
    const last = candles[candles.length - 1]?.close || 1;
    const precision = last >= 1000 ? 2 : last >= 1 ? 4 : Math.min(10, 3 - Math.floor(Math.log10(last)));
    candle.applyOptions({ priceFormat: { type: "price", precision, minMove: 10 ** -precision } });
    candle.setData(candles);
    vol.setData(candles.map((c) => ({ time: c.time, value: c.volume, color: c.close >= c.open ? "#00E67633" : "#FF3D0033" })));
  }, [candles]);

  useEffect(() => {
    const { markers } = refs.current;
    if (!markers || !candles.length) return;
    markers.setMarkers(buildMarkers(transfers, STEP[interval], candles[0].time));
  }, [transfers, candles, interval]);

  return (
    <div className="relative h-[460px] w-full" data-testid="lightweight-chart">
      <div ref={box} className="absolute inset-0" />
      {error && (
        <div className="absolute inset-0 flex items-center justify-center font-mono text-sm text-[#FFB300]" data-testid="chart-error">
          {error}
        </div>
      )}
    </div>
  );
}
